import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { collection, query, where, getDocs, doc, updateDoc, increment, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useToast } from '../components/ToastProvider';
import { LoadingState, ErrorState } from '../components/States';

const PasswordPrompt = ({ onCorrectPassword }) => {
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!password) {
            setError('กรุณากรอกรหัสผ่าน');
            return;
        }
        onCorrectPassword(password);
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
            <form onSubmit={handleSubmit} className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
                <h3 className="mb-4 text-xl font-bold text-slate-800">ลิงก์นี้มีการป้องกันด้วยรหัสผ่าน</h3>
                <label htmlFor="password" className="text-sm text-slate-600">
                    กรุณากรอกรหัสผ่านเพื่อดำเนินการต่อ:
                </label>
                <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-2 block w-full rounded-md border border-slate-300 p-2"
                />
                {error ? <p className="mt-2 text-sm text-red-500">{error}</p> : null}
                <button type="submit" className="mt-4 w-full rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white">
                    เข้าสู่ลิงก์
                </button>
            </form>
        </div>
    );
};

const RedirectHandler = () => {
    const { shortCode } = useParams();
    const { showToast } = useToast();

    const [status, setStatus] = useState('กำลังค้นหาลิงก์...');
    const [linkData, setLinkData] = useState(null);
    const [promptPassword, setPromptPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isInvalidLink, setIsInvalidLink] = useState(false);

    useEffect(() => {
        const findLink = async () => {
            if (!shortCode) return;
            setIsLoading(true);
            const q = query(collection(db, 'urls'), where('shortCode', '==', shortCode));
            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
                setStatus('ไม่พบลิงก์ที่ต้องการ');
                setIsInvalidLink(true);
                setIsLoading(false);
                return;
            }

            const urlDoc = querySnapshot.docs[0];
            const data = urlDoc.data();
            setLinkData({ id: urlDoc.id, ...data });

            if (data.expiresAt && data.expiresAt.toDate() < new Date()) {
                setStatus('ลิงก์นี้หมดอายุแล้ว');
                setIsInvalidLink(true);
                setIsLoading(false);
                return;
            }
            if (data.maxClicks > 0 && data.clickCount >= data.maxClicks) {
                setStatus('ลิงก์นี้ถูกคลิกครบจำนวนครั้งที่จำกัดแล้ว');
                setIsInvalidLink(true);
                setIsLoading(false);
                return;
            }
            if (data.password) {
                setPromptPassword(true);
                setIsLoading(false);
            } else {
                await recordClickAndRedirect(urlDoc.id, data.longUrl);
            }
        };
        findLink();
    }, [shortCode]);

    const recordClickAndRedirect = async (docId, longUrl) => {
        try {
            const urlDocRef = doc(db, 'urls', docId);
            await addDoc(collection(urlDocRef, 'clicks'), {
                timestamp: serverTimestamp(),
                userAgent: navigator.userAgent,
                referrer: document.referrer || 'Direct',
            });
            await updateDoc(urlDocRef, { clickCount: increment(1) });
        } catch (error) {
            console.error('Error recording click:', error);
        }
        window.location.href = longUrl;
    };

    const handlePasswordCheck = async (enteredPassword) => {
        if (enteredPassword === linkData.password) {
            setPromptPassword(false);
            setStatus('รหัสผ่านถูกต้อง กำลังเปลี่ยนเส้นทาง...');
            await recordClickAndRedirect(linkData.id, linkData.longUrl);
        } else {
            showToast('รหัสผ่านไม่ถูกต้อง', 'error');
        }
    };

    if (promptPassword) {
        return <PasswordPrompt onCorrectPassword={handlePasswordCheck} />;
    }

    if (isLoading) {
        return <LoadingState message="กำลังค้นหาลิงก์..." />;
    }

    if (isInvalidLink) {
        return <ErrorState message={status} />;
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-slate-50">
            <p className="text-slate-600">{status}</p>
        </div>
    );
};

export default RedirectHandler;
